import { isDefined } from '@aloft/toolkit-shared';

import { useCallback } from 'react';

import { dismissAlertToasts } from '../../hooks/useToaster/dismissAllToasts.ts';
import { useActiveReferenceBarID } from '../../controller/hooks/useActiveReferenceBarID.ts';
import { ReferenceViewID } from '../../modules/referenceView/index.ts';
import { DataSheet } from '../../modules/referenceView/types.ts';
import {
  clearAlertsPanelAutoOpenedForNavBlock,
  setActiveID,
  setFocusedAlertType,
  setMobileReferenceMenuOpen,
  setReferenceViewOpen,
  setSelectedDataSheet,
} from '../../redux/features/referenceBarUISlice.ts';
import { selectReferenceBarUI } from '../../redux/features/selectors/referenceBarUISelectors.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';

export const useTkv2HandleIconClick = (
  id?: ReferenceViewID,
): ((dataSheet: DataSheet | null) => void) => {
  const { selectedDataSheet } = useAppSelector(selectReferenceBarUI);
  const activeID = useActiveReferenceBarID();
  const dispatch = useAppDispatch();

  return useCallback(
    (dataSheet: DataSheet | null) => {
      const idChanged = id != activeID;
      const dataSheetChanged = dataSheet != selectedDataSheet;

      if (idChanged && isDefined(id)) {
        dispatch(setActiveID(id));
      }

      if (dataSheetChanged) {
        dispatch(setSelectedDataSheet(dataSheet));

        // If the datasheet changed, we've clicked on something new on
        // the reference bar, and we want the reference view to be open.
        // (If the active id changed, that means the user clicked on a
        // comp/subject - this doesn't indicate they want to change the
        // ref bar state, just change what is in it if it is open.)
        dispatch(setReferenceViewOpen(true));

        if (dataSheet === 'craftsman-alerts') {
          dispatch(clearAlertsPanelAutoOpenedForNavBlock());
          dismissAlertToasts();
          dispatch(setFocusedAlertType(null));
        }
      } else if (!idChanged || !isDefined(id)) {
        // If nothing changed (or we are on a top level icon), we're
        // closing the view that we last clicked on.
        dispatch(setReferenceViewOpen(false));
      }

      dispatch(setMobileReferenceMenuOpen(false));
    },
    [activeID, dispatch, id, selectedDataSheet],
  );
};
